import {store as AppStore} from '../../store/store';
import {LeadInputType} from '../../gql/pwa/graphql.ts';
import {formatISO, parse} from 'date-fns';
import {Planner} from '../../network/planner.ts';
import {getLeadProvider} from './get_lead_provider.ts';

export async function storeAppointmentData(store: typeof AppStore, formData: FormData) {
    const locationId = formData.get('location_id') as string;
    const specialtyId = formData.get('specialty_id');
    const vehicleId = formData.get('vehicle_id');
    const licensePlate = formData.get('license_plate');
    const mileage = formData.get('mileage');
    const mainActivityId = formData.get('main_activity_id');
    const subActivityIds = ((formData.get('sub_activity_ids') as string | null) ?? '').split(',').filter(Boolean);
    const replacementVehicleId = formData.get('replacement_vehicle_id');
    const remarks = formData.get('remarks');
    const date = formData.get('date');
    let time = formData.get('time') as string;
    let dateIso8601 = date && time && parse(time as string, 'H:m', parse(date as string, 'yyyy-MM-dd', new Date()));
    const me = store.getState().customerInfo.me;
    const customerId = me?.customer?.id || null;
    const customerEmail = me?.email || null;

    // If user selected "geen voorkeur", pick the first time from availability
    const noPreference = 'geen voorkeur';
    if (time.trim().toLowerCase() === noPreference) {
        const selectedDate = parse(date as string, 'yyyy-MM-dd', new Date());
        const availableTimesResp = await Planner.availableTimes(
            Number(locationId),
            selectedDate,
            String(mainActivityId),
            subActivityIds,
        );
        const plannerLocation = availableTimesResp.data.locations && availableTimesResp.data.locations[0];
        const specialty = plannerLocation?.specialties && plannerLocation.specialties[0];
        const availableTimes = (specialty?.available_times as string[] | undefined) ?? [];
        const firstCorrectTime = availableTimes.find((t) => t && t.trim().toLowerCase() !== noPreference) || null;
        if (firstCorrectTime) {
            time = firstCorrectTime;
            dateIso8601 = date && time && parse(time as string, 'H:m', parse(date as string, 'yyyy-MM-dd', new Date()));
        }
    }

    // The parsed time is the wall-clock time in the organisation's timezone.
    // Convert it to UTC so Edlink can correctly display the local time.
    const orgTimezone = store.getState().appConfig.organisation?.timezone;
    let dateUtcSameClock: Date | false = false;
    if (dateIso8601 instanceof Date && orgTimezone) {
        // Reconstruct the wall-clock time as a "naive UTC" timestamp
        const naiveUtcMs = Date.UTC(
            dateIso8601.getFullYear(),
            dateIso8601.getMonth(),
            dateIso8601.getDate(),
            dateIso8601.getHours(),
            dateIso8601.getMinutes(),
            dateIso8601.getSeconds(),
        );
        // Find what wall-clock time the org timezone shows for this naive UTC
        const orgWallClock = new Date(naiveUtcMs).toLocaleString('sv-SE', {timeZone: orgTimezone});
        const orgAsUtcMs = new Date(orgWallClock.replace(' ', 'T') + 'Z').getTime();
        // Subtract the offset to get the actual UTC time for the given org wall-clock time
        dateUtcSameClock = new Date(naiveUtcMs - (orgAsUtcMs - naiveUtcMs));
    } else if (dateIso8601 instanceof Date) {
        dateUtcSameClock = dateIso8601;
    }

    const leadType = 'appointment_request';
    const leadState = 'forwarded';
    const leadProviderId = await getLeadProvider(store);

    if (customerId === null || customerEmail === null) {
        throw new Error('No customer ID found');
    }
    if (
        locationId === null ||
        vehicleId === null ||
        mileage === null ||
        mainActivityId === null ||
        (!mainActivityId && subActivityIds.length === 0) ||
        replacementVehicleId === null ||
        remarks === null ||
        date === null ||
        time === null ||
        leadProviderId === null
    ) {
        throw new Error('Invalid data');
    }

    const leadData: LeadInputType = {
        customer_id: customerId,
        lead_provider_id: leadProviderId,
        lead_type: leadType,
        state: leadState,
        leadData: [
            {key: 'activity', value: mainActivityId as string},
            {key: 'sub_activities', value: JSON.stringify(subActivityIds)},
            {key: 'replacement_vehicle', value: replacementVehicleId as string},
            {key: 'license_plate', value: licensePlate as string},
            {key: 'mileage', value: mileage as string},
            {key: 'remarks', value: remarks as string},
            {key: 'specialty_id', value: specialtyId as string},
            {key: 'date_iso8601', value: (dateUtcSameClock && formatISO(dateUtcSameClock)) as string},
        ],
        customerData: [{key: 'email', value: customerEmail}],
    };
    await Planner.createAppointment(leadData);
}
